import * as React from 'react'
import { cn } from '../../lib/utils'
import { type PeriodValue } from '../core/period-picker'
import { PinnedWidget, type PinnedItem } from '../widgets/pinned-widget'
import {
  RecentlyVisitedWidget,
  type RecentlyVisitedItem,
} from '../widgets/recently-visited-widget'
import {
  UpcomingWidget,
  type UpcomingReminder,
} from '../widgets/upcoming-widget'
import { StatsWidget } from '../widgets/stats-widget'
import { PipelineWidget } from '../widgets/pipeline-widget'
import { ActivityTrendWidget } from '../widgets/activity-trend-widget'
import { ValueTrendWidget } from '../widgets/value-trend-widget'
import { PipelineTrendWidget } from '../widgets/pipeline-trend-widget'
import { TrendsWidget } from '../widgets/trends-widget'
import {
  type PerformanceStat,
  type PipelineStageDatum,
  type PerformanceTrends,
  type LineChartData,
} from '../widgets/performance-charts'
import { TabRoot, TabList, TabTrigger, TabContent } from '../core/tabs'

/**
 * Homepage — the homepage dashboard as a composition of the standalone
 * widgets, laid out responsively:
 *
 *   greeting + actions
 *   Pinned                         (full width)
 *   Recently Visited | Upcoming    (two-up, stacks on narrow)
 *   Stats (2×2) | Activity Trend        (two-up, stacks on narrow)
 *   Value Trend | Pipeline Trend        (two-up — `trendsLayout="separate"`)
 *   Pipeline by Stage              (full width)
 *
 * With `trendsLayout="combined"` the performance section is recomposed for a
 * homepage that can't yet hide or rearrange widgets: Stats becomes a narrow
 * 4-tile stack on the left, and the three trend charts collapse into one tabbed
 * `TrendsWidget` that stacks over Pipeline by Stage in a wider right column —
 * fewer, denser cards, with more horizontal room for both charts.
 *
 * Every section is its own widget (three "basics" — Pinned / Recently Visited /
 * Upcoming — and three "performance" — Stats / Pipeline / Trends), so a consumer
 * can rearrange, drop, or reuse any of them individually. The block just owns the
 * page layout; each widget owns its own card, header, and controls. Widgets fill
 * their layout cell (their own `max-w` caps are removed here).
 *
 *   <Homepage greeting="Good afternoon!" headerActions={<…>}
 *     pinned={pins} recentlyVisited={recent} reminders={reminders}
 *     stats={stats} pipeline={pipeline} trends={trends} onPeriodApply={reload} />
 */

/** Which widget a callback refers to (e.g. for `onHideWidget`). */
export type HomepageWidgetKey =
  | 'pinned'
  | 'recentlyVisited'
  | 'upcoming'
  | 'stats'
  | 'pipeline'
  | 'activityTrend'
  | 'valueTrend'
  | 'pipelineTrend'
  | 'trends'

export interface HomepageProps {
  /** Greeting / title above the widgets (e.g. "Good afternoon!"). */
  greeting?: React.ReactNode
  /** Right-aligned header controls (e.g. a summary picker) beside the greeting. */
  headerActions?: React.ReactNode

  pinned: PinnedItem[]
  /** Opens the Edit Pins modal from the Pinned widget's ⋯ menu. */
  onEditPins?: () => void
  onPinClick?: (item: PinnedItem) => void

  recentlyVisited: RecentlyVisitedItem[]
  onRecentClick?: (item: RecentlyVisitedItem) => void

  reminders: UpcomingReminder[]
  onReminderView?: (reminder: UpcomingReminder) => void
  onReminderEdit?: (reminder: UpcomingReminder) => void
  onReminderRemove?: (reminder: UpcomingReminder) => void

  stats: PerformanceStat[]
  pipeline: PipelineStageDatum[]
  /** Activity volume over time (bar chart) for the `ActivityTrendWidget`. */
  trends: PerformanceTrends
  /** Value Opened/Won over time (line chart) for the `ValueTrendWidget`. */
  valueTrend: LineChartData
  /** Pipeline value + deal count over time (dual-axis line) for `PipelineTrendWidget`. */
  pipelineTrend: LineChartData
  /**
   * How the trend charts are laid out. `'combined'` (default) collapses all three
   * into one tabbed `TrendsWidget` stacked over Pipeline by Stage beside a narrow
   * Stats column — the denser default. `'separate'` gives each chart its own card
   * (Activity Trend + Value Trend + Pipeline Trend), for a homepage that can hide
   * or rearrange widgets.
   */
  trendsLayout?: 'separate' | 'combined'
  /** Applied period, shared across the performance widgets. */
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void

  /** Hide a widget — called with the widget's key; wires each widget's ⋯ "Hide". */
  onHideWidget?: (widget: HomepageWidgetKey) => void
  /**
   * "View all" for a collection widget — called with the widget's key; wires the
   * ⋯ "View all" on Pinned / Recently Visited / Upcoming (navigate to the full
   * list). Omit to leave "View all" off those menus.
   */
  onViewAllWidget?: (widget: HomepageWidgetKey) => void

  /**
   * The Action Center feed — pass a configured `<ActionCenter>`. When provided,
   * the performance section moves under a **Performance** underline tab (selected
   * by default) and this renders under an **Action Center** tab beside it. Omit to
   * render the performance section directly with no tabs.
   */
  actionCenter?: React.ReactNode

  className?: string
}

/** Removes a widget's own max-width cap so it fills the layout cell. */
const FILL = 'max-w-none'

export const Homepage = React.forwardRef<
  HTMLDivElement,
  HomepageProps
>(
  (
    {
      greeting,
      headerActions,
      pinned,
      onEditPins,
      onPinClick,
      recentlyVisited,
      onRecentClick,
      reminders,
      onReminderView,
      onReminderEdit,
      onReminderRemove,
      stats,
      pipeline,
      trends,
      valueTrend,
      pipelineTrend,
      trendsLayout = 'combined',
      defaultPeriod,
      onPeriodApply,
      onHideWidget,
      onViewAllWidget,
      actionCenter,
      className,
    },
    ref
  ) => {
    const hide = (key: HomepageWidgetKey) =>
      onHideWidget ? () => onHideWidget(key) : undefined
    const viewAll = (key: HomepageWidgetKey) =>
      onViewAllWidget ? () => onViewAllWidget(key) : undefined

    // Stats + trend charts + pipeline. Rendered under the "Performance" tab when
    // an Action Center is supplied, else directly (no tabs). The wrapping flex-col
    // keeps the `separate` layout's rows spaced whichever way it's placed.
    const performanceSection = (
      <div className="flex flex-col gap-6">
        {trendsLayout === 'combined' ? (
          /* Compact layout — Stats as a narrow 4-tile stack on the left; the
             tabbed Trends card and Pipeline by stage stacked in a wider right
             column so both charts get more horizontal room. */
          <div className="grid gap-6 lg:grid-cols-3 lg:items-stretch">
            <StatsWidget
              className={cn(FILL, 'lg:col-span-1')}
              stats={stats}
              statColumns={1}
              defaultPeriod={defaultPeriod}
              onPeriodApply={onPeriodApply}
              onHide={hide('stats')}
            />
            <div className="flex flex-col gap-6 lg:col-span-2">
              <TrendsWidget
                className={cn(FILL, 'h-80')}
                activity={trends}
                value={valueTrend}
                pipeline={pipelineTrend}
                defaultPeriod={defaultPeriod}
                onPeriodApply={onPeriodApply}
                onHide={hide('trends')}
              />
              <PipelineWidget
                className={FILL}
                pipeline={pipeline}
                defaultPeriod={defaultPeriod}
                onPeriodApply={onPeriodApply}
                onHide={hide('pipeline')}
              />
            </div>
          </div>
        ) : (
          <>
            {/* Stats (2×2) beside Activity Trend — same height as the two
                lists above. */}
            <div className="grid gap-6 lg:grid-cols-2">
              <StatsWidget
                className={cn(FILL, 'h-64')}
                stats={stats}
                statColumns={2}
                defaultPeriod={defaultPeriod}
                onPeriodApply={onPeriodApply}
                onHide={hide('stats')}
              />
              <ActivityTrendWidget
                className={cn(FILL, 'h-64')}
                trends={trends}
                defaultPeriod={defaultPeriod}
                onPeriodApply={onPeriodApply}
                onHide={hide('activityTrend')}
              />
            </div>

            {/* The two line-based trends. */}
            <div className="grid items-stretch gap-6 lg:grid-cols-2">
              <ValueTrendWidget
                className={cn(FILL, 'h-64')}
                data={valueTrend}
                defaultPeriod={defaultPeriod}
                onPeriodApply={onPeriodApply}
                onHide={hide('valueTrend')}
              />
              <PipelineTrendWidget
                className={cn(FILL, 'h-64')}
                data={pipelineTrend}
                defaultPeriod={defaultPeriod}
                onPeriodApply={onPeriodApply}
                onHide={hide('pipelineTrend')}
              />
            </div>

            <PipelineWidget
              className={FILL}
              pipeline={pipeline}
              defaultPeriod={defaultPeriod}
              onPeriodApply={onPeriodApply}
              onHide={hide('pipeline')}
            />
          </>
        )}
      </div>
    )

    return (
      <div ref={ref} className={cn('flex w-full flex-col gap-6', className)}>
        {(greeting != null || headerActions != null) && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            {greeting != null ? (
              <h1 className="text-2xl font-bold text-fbui-gray-900">{greeting}</h1>
            ) : (
              <span />
            )}
            {headerActions != null && (
              <div className="flex items-center gap-2">{headerActions}</div>
            )}
          </div>
        )}

        <PinnedWidget
          className={FILL}
          size="sm"
          items={pinned}
          onEdit={onEditPins}
          onItemClick={onPinClick}
          onViewAll={viewAll('pinned')}
          onHide={hide('pinned')}
        />

        {/* Fixed, equal height for the two lists — capped short, each scrolls. */}
        <div className="grid gap-6 lg:grid-cols-2">
          <RecentlyVisitedWidget
            className={cn(FILL, 'h-52')}
            size="xs"
            items={recentlyVisited}
            onItemClick={onRecentClick}
            onViewAll={viewAll('recentlyVisited')}
            onHide={hide('recentlyVisited')}
          />
          <UpcomingWidget
            className={cn(FILL, 'h-52')}
            size="sm"
            reminders={reminders}
            onView={onReminderView}
            onEdit={onReminderEdit}
            onRemove={onReminderRemove}
            onViewAll={viewAll('upcoming')}
            onHide={hide('upcoming')}
          />
        </div>

        {actionCenter != null ? (
          /* Two underline tabs below the basics: Performance (the widgets,
             selected by default) and Action Center (the recommendation feed). */
          <TabRoot defaultValue="performance" variant="minimal">
            <TabList aria-label="Homepage sections">
              <TabTrigger value="performance">Performance</TabTrigger>
              <TabTrigger value="actionCenter">Action Center</TabTrigger>
            </TabList>
            <TabContent value="performance">{performanceSection}</TabContent>
            <TabContent value="actionCenter">{actionCenter}</TabContent>
          </TabRoot>
        ) : (
          performanceSection
        )}
      </div>
    )
  }
)
Homepage.displayName = 'Homepage'
